import React, { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Field, FieldType, UserData } from "../types";
import { FormField } from "./FormField";
import { CheckboxField } from "@kaltura/mediaspace-shared-ui/controlled-form";
import { translate } from "@kaltura/mediaspace-shared-utils";
import styled from "@emotion/styled";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

interface InviteFormProps {
    fields: Field[];
    disabledFields: Record<string, boolean>;
    requiredFields?: Record<string, boolean>;
    onUserSelected: (user: UserData) => void;
    onSubmit: (data: any) => Promise<void>;
    formId: string;
    showEmailOption?: boolean;
}

/**
 * Dedicated component for rendering and handling the invite user form logic.
 */
export const InviteForm: React.FC<InviteFormProps> = ({
    fields,
    disabledFields,
    onUserSelected,
    onSubmit,
    formId,
    showEmailOption = false,
    requiredFields,
}) => {
    const methods = useFormContext();
    const sendEmailChecked = methods.watch("sendEmail");
    const prevChecked = useRef(sendEmailChecked);
    const sendButtonAnalytics = useButtonAnalytics();

    useEffect(() => {
        if (
            showEmailOption
            && prevChecked.current !== undefined // skip initial undefined
            && prevChecked.current !== sendEmailChecked
        ) {
            sendButtonAnalytics("Invite user - toggle email send", ButtonClickAnalyticsType.TOGGLE);
        }
        prevChecked.current = sendEmailChecked;
    }, [sendEmailChecked, showEmailOption]);

    return (
        <form id={formId} onSubmit={methods.handleSubmit(onSubmit)}>
            {/* Render each form field */}
            {fields.map((field) => {
                if (field.type === FieldType.EMAIL_AUTOCOMPLETE) {
                    return (
                        <FormField
                            key={field.id}
                            disabled={disabledFields[field.id]}
                            onUserSelected={onUserSelected}
                            {...field}
                            required={requiredFields?.[field.id]}
                        />
                    );
                }

                return (
                    <FormField
                        key={field.id}
                        disabled={disabledFields[field.id]}
                        {...field}
                        required={requiredFields?.[field.id]}
                    />
                );
            })}
            {showEmailOption && <SendEmailCheckbox id={"sendEmail"} label={translate("Send link via Email")} />}
        </form>
    );
};

const SendEmailCheckbox = styled(CheckboxField)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));
